"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { KRUSKAL_WALLIS_TEST_CALCULATOR_STRINGS } from "@/components/tools/kruskal-wallis-test-calculator";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import { t } from "@/lib/i18n/interpolate";
import { median } from "@/lib/stats/descriptive";
import {
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import type { KruskalWallisResult } from "@/lib/tool-kit/stats-tests/kruskal-wallis";

/** Results for the Kruskal–Wallis calculator. */

type Strings = typeof KRUSKAL_WALLIS_TEST_CALCULATOR_STRINGS;

/** With fewer than five values in a group, the χ² approximation for H is rough. */
const SMALL_GROUP = 5;

export function KruskalWallisResults({
  r,
  data,
  names,
  alpha,
  strings,
  fmt,
}: {
  r: KruskalWallisResult;
  data: readonly (readonly number[])[];
  names: readonly string[];
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const apa = richTemplate(strings.apaTemplate, {
    df: fmt(r.df),
    h: fixed(r.h, 2, fmt),
    p: apaP(r.p, fmt),
    eps: fixed(r.epsilonSquared, 2, fmt, false),
  });
  const terms = r.rankSums
    .map((sum, i) => `${n(sum)}²/${fmt(r.sizes[i])}`)
    .join(" + ");
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.hStatistic} value={n(r.h)} />
        <StatCard label={strings.df} value={fmt(r.df)} />
        <StatCard emphasis label={strings.pValue} value={formatP(r.p, fmt)} />
        <StatCard label={strings.epsilonSquared} value={n(r.epsilonSquared)} />
        <StatCard label={strings.totalN} value={fmt(r.n)} />
        <StatCard label={strings.tieCorrection} value={n(r.tieCorrection)} />
      </StatGrid>
      <Decision reject={r.p < alpha}>
        {t(r.p < alpha ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(r.p, fmt),
        })}
      </Decision>
      {r.sizes.some((size) => size < SMALL_GROUP) ? (
        <p className={HINT}>{strings.smallSample}</p>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.ranksHeading}</h3>
      <ResultTable
        head={[
          strings.group,
          strings.n,
          strings.median,
          strings.rankSum,
          strings.meanRank,
        ]}
        rows={r.sizes.map((size, i) => ({
          key: `${i}-${names[i]}`,
          cells: [
            names[i],
            fmt(size),
            n(median(data[i])),
            n(r.rankSums[i]),
            n(r.meanRanks[i]),
          ],
        }))}
      />
      <h3 className={WIDGET_H3}>{strings.dunnHeading}</h3>
      <p className={HINT}>{t(strings.dunnHint, { m: fmt(r.dunn.length) })}</p>
      <ResultTable
        head={[
          strings.comparison,
          strings.rankDifference,
          strings.z,
          strings.p,
          strings.pAdjusted,
        ]}
        rows={r.dunn.map((pair) => ({
          key: `${pair.first}-${pair.second}`,
          cells: [
            t(strings.versus, { a: names[pair.first], b: names[pair.second] }),
            n(pair.meanRankDifference),
            n(pair.z),
            formatP(pair.p, fmt),
            formatP(pair.pAdjusted, fmt),
          ],
        }))}
      />
      <Working heading={strings.howCalculated}>
        <p>
          H′ = 12 / (N(N + 1)) × Σ Rⱼ²/nⱼ − 3(N + 1) = 12 / ({fmt(r.n)} ×{" "}
          {fmt(r.n + 1)}) × ({terms}) − 3 × {fmt(r.n + 1)} = {n(r.hUncorrected)}
        </p>
        <p>
          C = 1 − Σ(t³ − t) / (N³ − N) = 1 − {fmt(r.tieTerm)} /{" "}
          {fmt(r.n ** 3 - r.n)} = {n(r.tieCorrection)}
        </p>
        <p>
          H = H′ / C = {n(r.hUncorrected)} / {n(r.tieCorrection)} = {n(r.h)}; df
          = k − 1 = {fmt(r.df)}; p = P(χ²({fmt(r.df)}) ≥ {n(r.h)}) ={" "}
          {formatP(r.p, fmt)}
        </p>
        <p>
          ε² = H / (N − 1) = {n(r.h)} / {fmt(r.n - 1)} = {n(r.epsilonSquared)}
        </p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNote}
        parts={apa}
      />
    </section>
  );
}
